import axios from 'axios';
import { API_BASE } from './config';

export const API = API_BASE;

export const api = axios.create({
    baseURL: API_BASE,
    timeout: 20000,
    headers: { Accept: 'application/json' },
});

/**
 * Critical safety net: if a proxy / load balancer / WAF returns an HTML page
 * instead of JSON (most common reason for `.map is not a function` on EC2),
 * axios's default JSON transform will hand us a string. Detect & reject.
 */
api.interceptors.response.use(
    (resp) => {
        const data = resp.data;
        if (typeof data === 'string') {
            const head = data.trim().slice(0, 50).toLowerCase();
            if (head.startsWith('<!doctype') || head.startsWith('<html') || head.startsWith('<')) {
                // eslint-disable-next-line no-console
                console.error(
                    `[api] received HTML instead of JSON for ${resp.config?.url}. ` +
                    'Check REACT_APP_BACKEND_URL at build time AND nginx proxy_pass for /api.'
                );
                return Promise.reject(new Error('NON_JSON_RESPONSE'));
            }
        }
        return resp;
    },
    (err) => Promise.reject(err)
);

/**
 * Coerce any API response into a safe Array.
 * Handles: null/undefined, already-array, {data:[]}, {items:[]}, {results:[]},
 *          {anyKey:[]}, string/number/etc. -> [].
 */
export const toArray = (v) => {
    if (Array.isArray(v)) return v;
    if (v == null) return [];
    if (typeof v === 'object') {
        if (Array.isArray(v.data)) return v.data;
        if (Array.isArray(v.items)) return v.items;
        if (Array.isArray(v.results)) return v.results;
        for (const k of Object.keys(v)) {
            if (Array.isArray(v[k])) return v[k];
        }
    }
    return [];
};

/** Coerce any API response into a safe plain Object (never null/array/string). */
export const toObject = (v) => {
    if (v && typeof v === 'object' && !Array.isArray(v)) return v;
    return {};
};

const warnIfNotArray = (label, v) => {
    if (!Array.isArray(v)) {
        // eslint-disable-next-line no-console
        console.warn(`[api] expected array for ${label} but got`, typeof v, v);
    }
};

// ----------------- Endpoints -----------------

// List endpoints — guaranteed array
export const fetchCities = (featured) =>
    api.get('/cities', { params: { featured } })
        .then(r => { warnIfNotArray('cities', r.data); return toArray(r.data); })
        .catch(() => []);

export const fetchCity = (slug) =>
    api.get(`/cities/${slug}`).then(r => toObject(r.data)).catch(() => ({}));

export const fetchLocalities = (params) =>
    api.get('/localities', { params })
        .then(r => { warnIfNotArray('localities', r.data); return toArray(r.data); })
        .catch(() => []);

export const fetchLocalityDetail = (slug) =>
    api.get(`/localities/${slug}/detail`).then(r => toObject(r.data)).catch(() => ({}));

export const fetchProgrammaticPage = (slug, params = {}) =>
    api.get(`/programmatic-pages/${slug}`, { params })
        .then(r => toObject(r.data))
        .catch(() => ({}));

export const fetchProgrammaticPages = (params) =>
    api.get('/programmatic-pages', { params })
        .then(r => toArray(r.data?.items))
        .catch(() => []);

export const fetchDevelopers = (params) =>
    api.get('/developers', { params })
        .then(r => { warnIfNotArray('developers', r.data); return toArray(r.data); })
        .catch(() => []);

export const fetchDeveloper = (slug) =>
    api.get(`/developers/${slug}`)
        .then(r => {
            const d = toObject(r.data);
            return {
                developer: toObject(d.developer),
                projects: Array.isArray(d.projects) ? d.projects : [],
            };
        })
        .catch(() => ({ developer: {}, projects: [] }));

// Paginated — returns `{ total, items }`
export const fetchProperties = (params) =>
    api.get('/properties', { params })
        .then(r => {
            const d = toObject(r.data);
            return {
                total: typeof d.total === 'number' ? d.total : (Array.isArray(d.items) ? d.items.length : 0),
                items: Array.isArray(d.items) ? d.items : (Array.isArray(d) ? d : []),
            };
        })
        .catch(() => ({ total: 0, items: [] }));

export const fetchProperty = (slug) =>
    api.get(`/properties/${slug}`)
        .then(r => {
            const d = toObject(r.data);
            return {
                property: toObject(d.property),
                similar: Array.isArray(d.similar) ? d.similar : [],
            };
        })
        .catch(() => ({ property: null, similar: [] }));

export const fetchBanners = () =>
    api.get('/banners')
        .then(r => { warnIfNotArray('banners', r.data); return toArray(r.data); })
        .catch(() => []);

export const fetchBlogs = (params) =>
    api.get('/blogs', { params })
        .then(r => { warnIfNotArray('blogs', r.data); return toArray(r.data); })
        .catch(() => []);

export const fetchBlog = (slug) =>
    api.get(`/blogs/${slug}`)
        .then(r => {
            const d = toObject(r.data);
            return {
                blog: toObject(d.blog),
                related: Array.isArray(d.related) ? d.related : [],
            };
        })
        .catch(() => ({ blog: null, related: [] }));

export const fetchTestimonials = () =>
    api.get('/testimonials')
        .then(r => { warnIfNotArray('testimonials', r.data); return toArray(r.data); })
        .catch(() => []);

export const fetchAmenities = () =>
    api.get('/amenities')
        .then(r => { warnIfNotArray('amenities', r.data); return toArray(r.data); })
        .catch(() => []);

export const fetchStates = () =>
    api.get('/states')
        .then(r => { warnIfNotArray('states', r.data); return toArray(r.data); })
        .catch(() => []);

// Object response — `{ properties, cities, developers, happy_families }`
export const fetchStats = () =>
    api.get('/stats').then(r => toObject(r.data)).catch(() => ({}));

// Object response — `{ cities, localities, developers, projects }` (NOT wrapped in toArray)
export const searchSuggest = (q) =>
    api.get('/search/suggest', { params: { q } })
        .then(r => {
            const d = toObject(r.data);
            return {
                cities: Array.isArray(d.cities) ? d.cities : [],
                localities: Array.isArray(d.localities) ? d.localities : [],
                developers: Array.isArray(d.developers) ? d.developers : [],
                projects: Array.isArray(d.projects) ? d.projects : [],
            };
        })
        .catch(() => ({ cities: [], localities: [], developers: [], projects: [] }));

export const createLead = (payload) =>
    api.post('/leads', payload).then(r => toObject(r.data));

// ---- Public CMS reads (page sections + faqs) ----
export const fetchPageSections = (pageKey) =>
    api.get('/page-sections', { params: { page_key: pageKey } })
        .then(r => toArray(r.data))
        .catch(() => []);

export const fetchPageFaqs = (pageKey) =>
    api.get('/faqs', { params: { page_key: pageKey } })
        .then(r => toArray(r.data))
        .catch(() => []);
